/* eslint-disable @typescript-eslint/no-explicit-any */
import EventEmitter from "eventemitter2";
import { AudioGetter } from "./getter";
import AudioStream from "./stream";
import AudioProcessor from "./processer";
import { ErrorCodes } from "./constants";

export enum AudioRecordModes {
  MIX = "mix",
  MIC = "microphone",
  SYSTEM = "system",
}

export interface AudioRecordOptions {
  recordMode?: AudioRecordModes;
  autoChangeMic?: boolean;
}

export interface AudioRecordStartOptions {
  /**
   * 麦克风id
   */
  micId?: string;
  /**
   * 录制模式（麦克风、系统、混合）
   */
  mode?: AudioRecordModes;
}

export class WebrtcRecorder extends EventEmitter {
  private recording: boolean;
  private audioGetter: AudioGetter;
  private audioStream: AudioStream;
  private processer: AudioProcessor | null;
  private recordMode: AudioRecordModes;
  private currentMode: AudioRecordModes;
  private permission: boolean;
  private autoChangeMic: boolean;
  constructor(props?: AudioRecordOptions) {
    super();
    const { recordMode = AudioRecordModes.MIC, autoChangeMic = false } =
      props || {};
    this.recording = false;
    this.audioGetter = new AudioGetter();
    this.audioStream = new AudioStream();
    this.processer = null;
    this.recordMode = recordMode;
    this.currentMode = recordMode;
    this.autoChangeMic = autoChangeMic;
    this.permission = false;
  }

  public get type() {
    return "webrtc";
  }

  /**
   * 获取权限
   * @returns
   */
  public getpermission = async () => {
    const permission = await this.audioGetter.tryToGetpermission();
    this.permission = permission;
    return permission;
  };

  /**
   * 开始录制音频
   * @returns
   */
  public start = async (options?: AudioRecordStartOptions) => {
    const { micId, mode = this.recordMode } = options || {};
    this.currentMode = mode;
    if (this.recording) return;
    this.recording = true;

    try {
      if (!this.permission) {
        // 先请求权限
        const permission = await this.audioGetter.tryToGetpermission();
        if (!permission) {
          throw this._createError(ErrorCodes.EPERMISSION, "无权限");
        }
        this.permission = permission;
      }

      const streams: MediaStream[] = [];
      if (mode === AudioRecordModes.MIC || mode === AudioRecordModes.MIX) {
        const micStream = await this._getMicStream(micId);
        streams.push(micStream);
      } else if (
        mode === AudioRecordModes.SYSTEM ||
        mode === AudioRecordModes.MIX
      ) {
        const systemStream = await this._getSystemStream();
        streams.push(systemStream);
      }

      if (streams.length === 0) {
        throw this._createError(
          ErrorCodes.EAUDIOSTREAM,
          "无法获取获取音频，请检查设备音频"
        );
      }

      const stream = this.audioStream.merge(streams);
      this._recordMedia(stream);
      if (this.autoChangeMic) {
        this.audioGetter.listenMicrophoneChange(this._handleMicroponeChange);
      }
    } catch (error) {
      this.stop();
      this._handleError(error);
    }
  };

  /**
   * 暂停录制
   * 实际还是在录制，只是不再输出数据
   */
  public pause = () => {
    if (this.recording) {
      this.recording = false;
    }
  };

  /**
   * 继续录制
   * 从暂停状态恢复
   */
  public continue = () => {
    if (!this.recording) {
      this.recording = true;
    }
  };

  /**
   * 结束录制，释放资源
   */
  public stop = () => {
    this.recording = false;
    this.audioStream.dispose();
    this.processer?.dispose();
    this.audioGetter.offLiestenMicrophoneChange();
  };

  public switchMicrophone = (micId: string) => {
    if (!this.recording) {
      return;
    }
    this.stop();
    this.start({ micId, mode: this.currentMode });
  };

  private _getMicStream = async (micId?: string) => {
    if (!micId) {
      const defaultMic = await this.audioGetter.getDefautMicrophone();
      if (!defaultMic) {
        throw this._createError(ErrorCodes.EMIC, "未检测到麦克风设备");
      }
      micId = defaultMic.deviceId;
    }
    const micStream = await this.audioGetter.getMicrophoneAudioStream(micId);
    if (!micStream) {
      throw this._createError(ErrorCodes.EMICSTREAM, "未检测到麦克风音频流");
    }
    return micStream;
  };

  private _getSystemStream = async () => {
    const systemStream = await this.audioGetter.getSystemAudioStream();
    if (!systemStream) {
      throw this._createError(ErrorCodes.ESYSTEMSTREAM, "未获取到系统音频流");
    }
    return systemStream;
  };

  private _createError(code: ErrorCodes, errorMsg: string) {
    const error = new Error(errorMsg);
    (error as any).code = code;
    return error;
  }

  private _handleMicroponeChange = () => {
    this.stop();
    this.start();
  };

  private _recordMedia = (stream: MediaStream) => {
    this.processer = new AudioProcessor();
    this.processer.onMessage = (data) => {
      if (this.recording) {
        this.emit("data", data);
      }
    };
    this.processer.onError = (error) => {
      this._handleError(error);
    };
    this.processer.start(stream);
  };

  private _handleError = (error: any) => {
    this.emit("error", error);
  };
}
